import { styled } from '@mui/material';
import { useAuth } from 'contexts/auth';
import { useViewport } from 'contexts/viewport';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Box, Button, Container, Form, Logo, PasswordInput, Stack, Text } from 'shell/components';
import { getPasswordRequirements, isValidPassword, PASSWORD_REQUIREMENTS } from 'utils/password';

const UpdatePassword = () => {
    const { device } = useViewport();
    const { completeNewPassword } = useAuth();
    const history = useHistory();
    const location = useLocation();
    const email = location.state?.email || sessionStorage.getItem('timelygrader.pendingAuthEmail') || '';
    const [form, setForm] = useState({ password: '', confirmPassword: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const requirements = getPasswordRequirements(form.password);

    const submit = async () => {
        if (!isValidPassword(form.password)) return setError('Please satisfy all password requirements');
        if (form.password !== form.confirmPassword) return setError('Passwords do not match');
        setLoading(true);
        try {
            const result = await completeNewPassword(email, form.password);
            sessionStorage.removeItem('timelygrader.pendingAuthEmail');
            history.push(result.route === 'profile-set-up' ? '/profile-setup' : '/');
        } catch (caughtError) {
            setError('Your temporary password session has expired. Please sign in again.');
        } finally {
            setLoading(false);
        }
    };

    if (!email) {
        history.replace('/login');
        return null;
    }

    return (
        <Container device={device} sx={{ minHeight: '100%' }}>
            <PageStack alignItems={'center'}>
                <Logo device={'desktop'} />
                <Card device={device}>
                    <Stack alignItems={'center'} spacing={1}>
                        <Text component={'h1'} variant={'h6'} weight={'medium'}>
                            Update your password
                        </Text>
                        <Text variant={'p1'} align={'center'}>
                            Create a permanent password to finish signing in to your account.
                        </Text>
                    </Stack>
                    <Form onSubmit={submit}>
                        <Stack spacing={4} sx={{ mt: 7 }}>
                            <PasswordInput
                                autoComplete={'new-password'}
                                error={Boolean(error)}
                                helperText={error}
                                label={'Enter New Password'}
                                placeholder={'Enter new password'}
                                value={form.password}
                                onChange={(event) =>
                                    setForm((current) => ({ ...current, password: event.target.value }))
                                }
                                required
                            />
                            <Stack>
                                <Text variant={'p3'} color={'light'}>
                                    The password must have:
                                </Text>
                                {PASSWORD_REQUIREMENTS.map(({ key, label }) => (
                                    <Text key={key} variant={'p3'} color={requirements[key] ? 'light' : 'error'}>
                                        • {label}
                                    </Text>
                                ))}
                            </Stack>
                            <PasswordInput
                                autoComplete={'new-password'}
                                label={'Confirm Password'}
                                placeholder={'Enter new password'}
                                value={form.confirmPassword}
                                onChange={(event) =>
                                    setForm((current) => ({ ...current, confirmPassword: event.target.value }))
                                }
                                required
                            />
                            <Button loading={loading} type={'submit'}>
                                Update Password
                            </Button>
                        </Stack>
                    </Form>
                </Card>
            </PageStack>
        </Container>
    );
};

const PageStack = styled(Stack)(({ theme }) => ({ paddingTop: theme.spacing(18) }));
const Card = styled(Box)(({ theme, device }) => ({
    marginTop: theme.spacing(12),
    padding: device === 'mobile' ? 0 : theme.spacing(7),
    width: device === 'mobile' ? '100%' : 448,
    background: device === 'mobile' ? 'transparent' : theme.palette.ensightful.shades[0],
    boxShadow: device === 'mobile' ? 'none' : theme.palette.ensightful.shadows[1],
    borderRadius: '20px',
}));

export default UpdatePassword;
